import { useDeferredValue, useMemo, useState } from 'react';
import type { CustomerSensor } from '@metron/shared';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Checkbox } from '../ui/checkbox';
import { Search, X } from 'lucide-react';

export interface ConsumerMeterPickerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sensors: CustomerSensor[];
  existingSensorIds: Set<number>;
  onAdd: (sensorIds: number[]) => void;
}

/**
 * Search-first: with large customers (thousands of meters) rendering the full
 * list is unusable, so no results are shown until the user types, and matches
 * are capped at MAX_RESULTS with a "refine your search" notice.
 */
const MAX_RESULTS = 50;

export function ConsumerMeterPicker({
  open,
  onOpenChange,
  sensors,
  existingSensorIds,
  onAdd,
}: ConsumerMeterPickerProps) {
  const [search, setSearch] = useState('');
  // Map (not Set) so selected meters can render deviceId/address even after
  // the search query changes and they drop out of the result list.
  const [selected, setSelected] = useState<Map<number, CustomerSensor>>(
    new Map(),
  );
  // Keep typing responsive while filtering large sensor arrays.
  const deferredSearch = useDeferredValue(search);

  const query = deferredSearch.trim().toLowerCase();

  const matches = useMemo(() => {
    if (!query) return [];
    const results: CustomerSensor[] = [];
    for (const s of sensors) {
      if (
        s.deviceId.toLowerCase().includes(query) ||
        s.address?.toLowerCase().includes(query)
      ) {
        results.push(s);
        if (results.length > MAX_RESULTS) break;
      }
    }
    return results;
  }, [sensors, query]);

  const overflow = matches.length > MAX_RESULTS;
  const visible = overflow ? matches.slice(0, MAX_RESULTS) : matches;
  const selectedSensors = useMemo(() => Array.from(selected.values()), [selected]);

  const handleToggle = (sensor: CustomerSensor) => {
    setSelected((prev) => {
      const next = new Map(prev);
      if (next.has(sensor.sensorId)) {
        next.delete(sensor.sensorId);
      } else {
        next.set(sensor.sensorId, sensor);
      }
      return next;
    });
  };

  const handleRemove = (sensorId: number) => {
    setSelected((prev) => {
      const next = new Map(prev);
      next.delete(sensorId);
      return next;
    });
  };

  const reset = () => {
    setSelected(new Map());
    setSearch('');
  };

  const handleAdd = () => {
    onAdd(Array.from(selected.keys()));
    reset();
    onOpenChange(false);
  };

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) reset();
    onOpenChange(nextOpen);
  };

  const selectedCount = selected.size;
  const addLabel = selectedCount === 0
    ? 'Add meter(s)'
    : selectedCount === 1
    ? 'Add 1 meter'
    : `Add ${selectedCount} meters`;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className='max-w-2xl max-h-[80vh] flex flex-col'>
        <DialogHeader>
          <DialogTitle>Add Meters</DialogTitle>
          <DialogDescription>
            Search for meters to add to this consumer's account.
          </DialogDescription>
        </DialogHeader>

        <div className='relative'>
          <Search className='absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground' />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder='Search by device ID or address...'
            className='pl-9'
          />
        </div>

        <div className='flex-1 overflow-y-auto border rounded-md min-h-[160px] max-h-[320px]'>
          {!query
            ? (
              <div className='flex items-center justify-center py-8'>
                <p className='text-sm text-muted-foreground'>
                  Type to search this customer's meters
                </p>
              </div>
            )
            : visible.length === 0
            ? (
              <div className='flex items-center justify-center py-8'>
                <p className='text-sm text-muted-foreground'>
                  No meters match your search
                </p>
              </div>
            )
            : (
              <div className='divide-y'>
                {visible.map((sensor) => {
                  const isExisting = existingSensorIds.has(sensor.sensorId);
                  const isSelected = selected.has(sensor.sensorId);

                  return (
                    <label
                      key={sensor.sensorId}
                      className={`flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-muted/50 ${
                        isExisting ? 'opacity-50 cursor-not-allowed' : ''
                      }`}
                    >
                      <Checkbox
                        checked={isSelected}
                        onCheckedChange={() => handleToggle(sensor)}
                        disabled={isExisting}
                      />
                      <div className='flex-1 min-w-0'>
                        <span className='text-sm font-mono'>{sensor.deviceId}</span>
                        {sensor.address && (
                          <span className='text-xs text-muted-foreground ml-2'>
                            {sensor.address}
                          </span>
                        )}
                      </div>
                      {isExisting && (
                        <span className='text-xs text-muted-foreground shrink-0'>
                          Already assigned
                        </span>
                      )}
                    </label>
                  );
                })}
                {overflow && (
                  <p className='p-2 text-xs text-muted-foreground text-center bg-muted'>
                    Showing first {MAX_RESULTS} matches — refine your search.
                  </p>
                )}
              </div>
            )}
        </div>

        {selectedCount > 0 && (
          <div className='border rounded-md'>
            <p className='px-3 py-1.5 text-xs font-medium text-muted-foreground border-b'>
              Selected ({selectedCount})
            </p>
            <ul className='max-h-[140px] overflow-y-auto divide-y' aria-label='Selected meters'>
              {selectedSensors.map((sensor) => (
                <li
                  key={sensor.sensorId}
                  className='flex items-center gap-3 px-3 py-1.5'
                >
                  <div className='flex-1 min-w-0'>
                    <span className='text-sm font-mono'>{sensor.deviceId}</span>
                    {sensor.address && (
                      <span className='text-xs text-muted-foreground ml-2'>
                        {sensor.address}
                      </span>
                    )}
                  </div>
                  <Button
                    variant='ghost'
                    size='icon'
                    className='h-6 w-6 shrink-0'
                    aria-label={`Remove ${sensor.deviceId}`}
                    onClick={() => handleRemove(sensor.sensorId)}
                  >
                    <X className='h-3.5 w-3.5' />
                  </Button>
                </li>
              ))}
            </ul>
          </div>
        )}

        <DialogFooter className='flex-row gap-2 sm:justify-between'>
          <span className='text-sm text-muted-foreground self-center'>
            {selectedCount > 0 ? `${selectedCount} selected` : 'None selected'}
          </span>
          <div className='flex gap-2'>
            <Button
              variant='outline'
              onClick={() => handleOpenChange(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={handleAdd}
              disabled={selectedCount === 0}
            >
              {addLabel}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
